import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Chip, Button,
  Skeleton, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField,
} from '@mui/material';
import { School, Add, CheckCircle, Cancel, Star } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import knowledgeService, { KnowledgeSession } from '../../services/knowledgeService';
import ConfirmDialog from '../../components/shared/ConfirmDialog';
import AppSnackbar from '../../components/shared/AppSnackbar';
import { colors } from '../../theme/theme';

const statusColors: Record<string, string> = {
  pending_approval: colors.warning.main,
  open: colors.success.main,
  confirmed: colors.primary.main,
  completed: colors.neutral[400],
  cancelled: colors.error.main,
  rejected: colors.error.main,
};

const statusLabels: Record<string, string> = {
  pending_approval: 'Pending Approval',
  open: 'Open for Sign-ups',
  confirmed: 'Confirmed',
  completed: 'Completed',
  cancelled: 'Cancelled',
  rejected: 'Rejected',
};

export default function MyKnowledgeSessionsPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [sessions, setSessions] = useState<KnowledgeSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [rateDialog, setRateDialog] = useState<{ eventId: string; studentName: string; studentId: string } | null>(null);
  const [ratings, setRatings] = useState({ preparedness: 4, engagement: 4, punctuality: 4, comment: '' });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'error' as 'error' | 'success' | 'info' });
  const [confirmDlg, setConfirmDlg] = useState<{ open: boolean; title: string; message: string; onConfirm: () => void }>({ open: false, title: '', message: '', onConfirm: () => { /* no-op */ } });

  useEffect(() => {
    knowledgeService.getMySessions().then(setSessions)
      .catch((err) => { console.warn('[MyKnowledgeSessionsPage] load sessions failed', err); })
      .finally(() => setLoading(false));
  }, []);

  const handleAccept = async (eventId: string) => {
    try {
      await knowledgeService.accept(eventId);
      setSessions(s => s.map(x => x.id === eventId ? { ...x, status: 'confirmed' } : x));
    } catch (e: any) { console.warn('[MyKnowledgeSessionsPage] accept failed', e); setSnackbar({ open: true, message: e.message, severity: 'error' }); }
  };

  const handleCancel = (eventId: string) => {
    setConfirmDlg({
      open: true,
      title: 'Cancel Session',
      message: 'Cancel this session? Students will be refunded.',
      onConfirm: async () => {
        setConfirmDlg(d => ({ ...d, open: false }));
        try {
          await knowledgeService.cancel(eventId);
          setSessions(s => s.map(x => x.id === eventId ? { ...x, status: 'cancelled' } : x));
        } catch (e: any) { console.warn('[MyKnowledgeSessionsPage] cancel failed', e); setSnackbar({ open: true, message: e.message, severity: 'error' }); }
      },
    });
  };

  const handleRateStudent = async () => {
    if (!rateDialog) return;
    try {
      await knowledgeService.rateStudent(rateDialog.eventId, {
        student_user_id: rateDialog.studentId,
        ...ratings,
      });
      setRateDialog(null);
      setSnackbar({ open: true, message: 'Student rated successfully!', severity: 'success' });
    } catch (e: any) { console.warn('[MyKnowledgeSessionsPage] rate student failed', e); setSnackbar({ open: true, message: e.message, severity: 'error' }); }
  };

  const active = sessions.filter(s => ['pending_approval', 'open', 'confirmed'].includes(s.status));
  const past = sessions.filter(s => ['completed', 'cancelled', 'rejected'].includes(s.status));

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>{t('pages.knowledge.my_sessions_title')}</Typography>
          <Typography color="text.secondary">{t('pages.knowledge.my_sessions_subtitle')}</Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/knowledge/create')}>
          New Session
        </Button>
      </Box>

      {loading ? (
        [1,2].map(i => <Skeleton key={i} height={120} sx={{ mb: 2, borderRadius: 3 }} />)
      ) : sessions.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <School sx={{ fontSize: 48, color: colors.neutral[300], mb: 2 }} />
          <Typography variant="h6" color="text.secondary">No sessions yet</Typography>
          <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate('/knowledge/create')}>
            Create Your First Session
          </Button>
        </Paper>
      ) : (
        <>
          {/* Active Sessions */}
          {active.length > 0 && (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>Active ({active.length})</Typography>
              {active.map(s => (
                <Paper key={s.id} sx={{ p: 3, mb: 2, borderRadius: 3, borderLeft: `4px solid ${statusColors[s.status] || colors.neutral[300]}` }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700 }}>{s.title}</Typography>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.5, flexWrap: 'wrap' }}>
                        <Chip label={statusLabels[s.status]} size="small"
                          sx={{ bgcolor: statusColors[s.status], color: 'white', fontWeight: 600 }} />
                        <Typography variant="body2" color="text.secondary">
                          {s.scheduled_date} at {s.scheduled_time?.slice(0,5)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {s.current_count}/{s.max_capacity} students
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 1 }}>
                        {s.topic_tags?.map(t => <Chip key={t} label={t} size="small" sx={{ fontSize: '0.7rem' }} />)}
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {s.status === 'open' && s.current_count >= s.min_capacity && (
                        <Button size="small" variant="contained" color="success" startIcon={<CheckCircle />}
                          onClick={() => handleAccept(s.id)}>Accept</Button>
                      )}
                      {['pending_approval', 'open'].includes(s.status) && (
                        <IconButton color="error" aria-label="Cancel session" onClick={() => handleCancel(s.id)}>
                          <Cancel />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: colors.primary.main }}>
                      {s.knowledge_fee} AZN knowledge fee
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </>
          )}

          {/* Past Sessions */}
          {past.length > 0 && (
            <>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, mt: 3 }}>Past ({past.length})</Typography>
              {past.map(s => (
                <Paper key={s.id} sx={{ p: 3, mb: 2, borderRadius: 3, opacity: 0.8 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                      <Typography sx={{ fontWeight: 600 }}>{s.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {s.scheduled_date} · {s.current_count} students · {statusLabels[s.status]}
                      </Typography>
                      {s.avg_rating && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                          <Star sx={{ fontSize: 16, color: '#F2994A' }} />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.avg_rating.toFixed(1)}</Typography>
                        </Box>
                      )}
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: colors.primary.main }}>
                      {s.knowledge_fee * s.current_count} AZN earned
                    </Typography>
                  </Box>
                </Paper>
              ))}
            </>
          )}
        </>
      )}

      {/* Rate Student Dialog */}
      <Dialog open={!!rateDialog} onClose={() => setRateDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Rate Student: {rateDialog?.studentName}</DialogTitle>
        <DialogContent>
          {['preparedness', 'engagement', 'punctuality'].map(dim => (
            <Box key={dim} sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, textTransform: 'capitalize' }}>{dim}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[1,2,3,4,5].map(n => (
                  <IconButton key={n} size="small" aria-label={`Rate ${dim} ${n} of 5`} onClick={() => setRatings(r => ({ ...r, [dim]: n }))}
                    // eslint-disable-next-line security/detect-object-injection -- dim iterates the literal array ['preparedness','engagement','punctuality'].
                    sx={{ color: n <= (ratings as any)[dim] ? '#F2994A' : colors.neutral[300] }}>
                    <Star />
                  </IconButton>
                ))}
              </Box>
            </Box>
          ))}
          <TextField fullWidth multiline rows={2} label="Comment (optional)"
            value={ratings.comment} onChange={e => setRatings(r => ({ ...r, comment: e.target.value }))} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRateDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleRateStudent}>Submit Rating</Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog open={confirmDlg.open} title={confirmDlg.title} message={confirmDlg.message} onConfirm={confirmDlg.onConfirm} onCancel={() => setConfirmDlg(d => ({ ...d, open: false }))} />
      <AppSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))} />
    </Container>
  );
}
